import type { SignupAttribution } from "./signup-attribution";

// Human-readable "where did this merchant come from" labels shown on the Slack
// installs channel and admin surfaces. Referral and ad sources coexist.
// "partner" is the stored kind for referrals; it is saved in Reclaim settings.
export const installAttributionKinds = [
  "meta_ad",
  "google_ad",
  "organic_search",
  "content",
  "paid_other",
  "partner",
] as const;
export type InstallAttributionKind = (typeof installAttributionKinds)[number];

export interface InstallAttributionSource {
  kind: InstallAttributionKind;
  label: string;
  referralCode?: string;
}

interface InstallAttributionReferrer {
  referrerName: string;
  referralCode?: string;
}

const META_UTM_SOURCES = new Set(["facebook", "fb", "instagram", "ig", "meta"]);
const GOOGLE_UTM_SOURCES = new Set(["google", "adwords", "googleads"]);

export function deriveInstallAttribution(
  attribution: SignupAttribution | undefined,
  referrer?: InstallAttributionReferrer,
): InstallAttributionSource[] {
  const sources: InstallAttributionSource[] = [];
  if (referrer) {
    sources.push({
      kind: "partner",
      label: referrer.referrerName,
      ...(referrer.referralCode ? { referralCode: referrer.referralCode } : {}),
    });
  }
  const utmSource = attribution?.utmSource?.toLowerCase();
  const utmMedium = attribution?.utmMedium?.toLowerCase();
  const isMeta =
    !!attribution?.fbclid || (!!utmSource && META_UTM_SOURCES.has(utmSource));
  const isGoogle =
    !!attribution?.gclid || (!!utmSource && GOOGLE_UTM_SOURCES.has(utmSource));
  if (isMeta) {
    sources.push({ kind: "meta_ad", label: "Meta Ads" });
  }
  if (isGoogle) {
    sources.push({ kind: "google_ad", label: "Google Ads" });
  }
  if (!isMeta && !isGoogle) {
    // Blog CTAs tag app links utm_source=blog (both cases), adding
    // utm_medium=organic only for search arrivals; utm_campaign is the article.
    const article = attribution?.utmCampaign
      ? ` — ${attribution.utmCampaign}`
      : "";
    if (utmSource === "blog") {
      sources.push(
        utmMedium === "organic"
          ? { kind: "organic_search", label: `Organic search${article}` }
          : { kind: "content", label: `Blog${article}` },
      );
    } else if (attribution?.utmSource) {
      sources.push({
        kind: "paid_other",
        label: `Paid: ${attribution.utmSource}`,
      });
    }
  }
  return sources;
}

const SLACK_AD_EMOJI: Partial<
  Record<InstallAttributionSource["kind"], string>
> = { meta_ad: ":meta:", google_ad: ":google-ads:" };

export function formatInstallAttributionForSlack(
  sources: InstallAttributionSource[],
): string | undefined {
  if (!sources.length) {
    return undefined;
  }
  return sources
    .map((source) => {
      const emoji =
        source.kind === "partner" &&
        source.referralCode?.trim().toUpperCase() === "SLASH"
          ? ":slash:"
          : SLACK_AD_EMOJI[source.kind];
      return emoji ? `${source.label} ${emoji}` : source.label;
    })
    .join(" • ");
}
